"use client";

import React from 'react';
import VideoGalleryCard, { type VideoGalleryCardProps } from '@/components/VideoGalleryCard';
import { buildClientUrl } from '@/lib/api';
import { apiRoutes } from '@/lib/apiRoutes';

export default function VideoMoreFromClient({ personId, excludeId }: { personId?: number | null; excludeId?: number | string }) {
  const [items, setItems] = React.useState<VideoGalleryCardProps[] | null>(null);
  React.useEffect(() => {
    if (!personId || personId <= 0) return;
    const controller = new AbortController();
    (async () => {
      try {
        // Resolve uploader GUID from numeric personId, then fetch canonical v2 videos for that uploader
        const ures = await fetch(buildClientUrl(apiRoutes.personById(String(personId))), { cache: 'no-store', signal: controller.signal });
        if (!ures.ok) { setItems([]); return; }
        const pj = await ures.json().catch(() => ({}));
        const userGuid = pj?.userId || pj?.UserId;
        if (!userGuid) { setItems([]); return; }
        const vres = await fetch(`/api/video/v2?userId=${encodeURIComponent(String(userGuid))}&take=9`, { cache: 'no-store', signal: controller.signal });
        if (!vres.ok) { setItems([]); return; }
        const list = await vres.json();
        const mapped = (Array.isArray(list) ? list : []).map((v: any) => ({
          id: Number(v?.id ?? v?.Id ?? 0),
          title: String(v?.title ?? v?.Title ?? ''),
          thumbnailUrl: String(v?.thumbnailUrl ?? v?.ThumbnailUrl ?? ''),
          viewCount: typeof v?.viewCount === 'number' ? v.viewCount : (typeof v?.ViewCount === 'number' ? v.ViewCount : null),
          durationSeconds: typeof v?.durationSeconds === 'number' ? v.durationSeconds : null,
          unsafe: Boolean(v?.isUnsafeContent ?? v?.IsUnsafeContent ?? false),
        })) as VideoGalleryCardProps[];
        const filtered = mapped.filter(v => v.id && String(v.id) !== String(excludeId)).slice(0, 6);
        setItems(filtered);
      } catch {
        setItems([]);
      }
    })();
    return () => controller.abort();
  }, [personId, excludeId]);

  if (!personId || personId <= 0) return null;

  // Hide section entirely when there are no items to show
  if (Array.isArray(items) && items.length === 0) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold">More from this creator</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items === null && (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl border bg-card h-32 skeleton" />
          ))
        )}
        {Array.isArray(items) && items.map((it, i) => (
          <VideoGalleryCard key={`${it.id}-${i}`} {...it} />
        ))}
      </div>
    </section>
  );
}

